import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import * as z from 'zod'
import { GridFeeState, PowerPriceState } from '../dto'
import { isAfter } from 'date-fns'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'
import {
  isTimeAfter,
  isWholeDayCovered,
  isWholeYearCovered,
  checkIsNotMoreThanTwoDecimalPoint,
  checkIsNotMoreThanThreeDecimalPoint,
  hasSelectedWeekday,
  isEveryWeekdayCovered,
  hasOverlappingWeekdayPeriods,
  hasAtLeastTwoDistinctPrices,
} from '../utils'
import { MAX_MANUAL_POWER_PRICE_PERIODS, SurchargeUnit } from '../constants'

interface ReturnType {
  dynamicTariffSettingsFormSchema: z.Schema
}

/**
 * Hook to validate dynamic tariff settings form with zod
 */
const useValidateDynamicTariffSettingsForm = (): ReturnType => {
  const { t } = useCustomTranslation()
  const requiredMessage = t(ValidationMessageKeys.required)
  const minimumValueIsZeroMessage = t(ValidationMessageKeys.min, { value: 0 })
  const minimumValueIsOneMessage = t(ValidationMessageKeys.min, { value: 1 })
  const maximumValueIs100Message = t(ValidationMessageKeys.max, { value: 100 })
  const toAfterFromMessage = t('validation.error-message.to-after-from')
  const maximumDecimalPointsIsTwoMessage = t('validation.error-message.maximum-decimal-points-allowed', { value: 2 })
  const maximumDecimalPointsIsThreeMessage = t('validation.error-message.maximum-decimal-points-allowed', { value: 3 })
  const invalidTimeMessage = t('dynamic-tariff-settings.validation.invalid-quarter-step-time')
  const maximumThirtyPeriodsMessage = t(ValidationMessageKeys.arrayMax, { count: MAX_MANUAL_POWER_PRICE_PERIODS })
  const incompleteCoverageMessage = t('dynamic-tariff-settings.manual.validation.incomplete-coverage')
  const overlappingPeriodsMessage = t('dynamic-tariff-settings.manual.validation.overlapping-periods')
  const atLeastTwoDistinctPricesMessage = t('dynamic-tariff-settings.manual.validation.at-least-two-different-prices')

  const twoDecimalPointPositiveNumberSchema = z
    .number({ message: requiredMessage })
    .min(0, { message: minimumValueIsZeroMessage })
    .refine(checkIsNotMoreThanTwoDecimalPoint, {
      message: maximumDecimalPointsIsTwoMessage,
    })

  const twoDecimalPointSurchargePercentSchema = z
    .number({ message: requiredMessage })
    .min(0, { message: minimumValueIsZeroMessage })
    .max(100, { message: maximumValueIs100Message })
    .refine(checkIsNotMoreThanTwoDecimalPoint, {
      message: maximumDecimalPointsIsTwoMessage,
    })

  const threeDecimalPointPositiveNumberSchema = z
    .number({ message: requiredMessage })
    .min(0, { message: minimumValueIsZeroMessage })
    .refine(checkIsNotMoreThanThreeDecimalPoint, {
      message: maximumDecimalPointsIsThreeMessage,
    })

  const timeFormatSchema = z.date({ message: requiredMessage }).refine(
    value => {
      const minutes = value.getMinutes()
      const hours = value.getHours()
      /* minutes must be 0, 15, 30 or 45, unless it's 23:59 */
      return minutes % 15 === 0 || (hours === 23 && minutes === 59)
    },
    {
      message: invalidTimeMessage,
    }
  )

  const timeFrameSchema = z
    .object({
      from: timeFormatSchema,
      to: timeFormatSchema,
      price: twoDecimalPointPositiveNumberSchema,
      description: z.string(),
    })
    .refine(
      value => {
        return isTimeAfter(value.to, value.from)
      },
      {
        message: toAfterFromMessage,
        path: ['to'],
      }
    )

  const dateFrameSchema = z
    .object({
      from: z.date({ message: requiredMessage }),
      to: z.date({ message: requiredMessage }),
      timeFrames: z
        .array(timeFrameSchema)
        .nonempty({ message: minimumValueIsOneMessage })
        .refine(
          value => {
            return isWholeDayCovered(value)
          },
          {
            message: 'invalid time range',
          }
        ),
    })
    .refine(
      value => {
        return isAfter(value.to, value.from)
      },
      {
        message: toAfterFromMessage,
        path: ['to'],
      }
    )

  const DateFrameListSchema = z
    .array(dateFrameSchema)
    .nonempty({ message: minimumValueIsOneMessage })
    .refine(
      value => {
        return isWholeYearCovered(value)
      },
      {
        message: 'invalid date range',
      }
    )

  const weekdaysSchema = z
    .object({
      mo: z.boolean(),
      tu: z.boolean(),
      we: z.boolean(),
      th: z.boolean(),
      fr: z.boolean(),
      sa: z.boolean(),
      su: z.boolean(),
    })
    .refine(hasSelectedWeekday, { message: requiredMessage })

  const manualPowerPriceSchema = z
    .object({
      weekdays: weekdaysSchema,
      from: timeFormatSchema,
      to: timeFormatSchema,
      price: threeDecimalPointPositiveNumberSchema,
      description: z.string().optional(),
    })
    .refine(
      value => {
        return isTimeAfter(value.to, value.from)
      },
      {
        message: toAfterFromMessage,
        path: ['to'],
      }
    )

  const ManualPowerPriceListSchema = z
    .array(manualPowerPriceSchema)
    .max(MAX_MANUAL_POWER_PRICE_PERIODS, { message: maximumThirtyPeriodsMessage })
    .superRefine((periods, ctx) => {
      /* overlaps take precedence: they have a dedicated message, and an overlap also fails coverage */
      if (hasOverlappingWeekdayPeriods(periods)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: overlappingPeriodsMessage })
        return
      }
      /* the same price may be reused, but not every period may share a single price (static tariff) */
      if (!hasAtLeastTwoDistinctPrices(periods)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: atLeastTwoDistinctPricesMessage })
        return
      }
      if (!isEveryWeekdayCovered(periods)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: incompleteCoverageMessage })
      }
    })

  const GridFeeSchema = z.discriminatedUnion('gridFeeState', [
    z.object({
      gridFeeState: z.literal(GridFeeState.Variable),
      gridFees: DateFrameListSchema,
    }),
    z.object({
      gridFeeState: z.literal(GridFeeState.Static),
      gridFees: twoDecimalPointPositiveNumberSchema,
    }),
    z.object({
      gridFeeState: z.literal(GridFeeState.Included),
      gridFees: z.null(),
    }),
  ])

  const PowerPriceSchema = z
    .discriminatedUnion('powerPriceState', [
      z.object({
        powerPriceState: z.literal(PowerPriceState.Manual),
        powerPrices: ManualPowerPriceListSchema,
        vatPercent: twoDecimalPointSurchargePercentSchema,
      }),
      z.object({
        powerPriceState: z.literal(PowerPriceState.Static),
        powerPrices: twoDecimalPointPositiveNumberSchema,
        vatPercent: twoDecimalPointSurchargePercentSchema,
      }),
      z.object({
        powerPriceState: z.literal(PowerPriceState.Dynamic),
        taxes: twoDecimalPointPositiveNumberSchema,
        vatPercent: twoDecimalPointSurchargePercentSchema,
        surchargeUnit: z.nativeEnum(SurchargeUnit),
        /* only the field matching surchargeUnit is validated below; the inactive one is ignored and nulled by the transformer */
        surchargePercent: z.number().min(0).nullable(),
        surchargeValue: z.number().min(0).nullable(),
      }),
    ])
    .superRefine((data, ctx) => {
      if (data.powerPriceState !== PowerPriceState.Dynamic) return

      const isCtPerKwh = data.surchargeUnit === SurchargeUnit.CtPerKwh
      const activeField = isCtPerKwh ? 'surchargeValue' : 'surchargePercent'
      const activeValue = isCtPerKwh ? data.surchargeValue : data.surchargePercent
      const activeSchema = isCtPerKwh ? twoDecimalPointPositiveNumberSchema : twoDecimalPointSurchargePercentSchema

      const result = activeSchema.safeParse(activeValue)
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: [activeField] })
        }
      }
    })

  const dynamicTariffSettingsFormSchema = z.intersection(GridFeeSchema, PowerPriceSchema)

  return { dynamicTariffSettingsFormSchema }
}

export default useValidateDynamicTariffSettingsForm
